import { Color, SRGBColorSpace, LinearSRGBColorSpace } from "three";
import { materialAssignments } from "../logic/material_slots.js";

const EXTENSION = "INNOWORKS_configurables";
const copy = value => structuredClone(value);
const aliases = {
    color: ["basecolorfactor", "basecolor", "color"],
    metallic: ["metallicfactor", "metallic"],
    roughness: ["roughnessfactor", "roughness", "smoothness"],
    clearcoat: ["clearcoatfactor"],
    clearcoatRoughness: ["clearcoatroughnessfactor"]
};

// Resolve the live assignment, including a finish applied by another State.
// GLTFLoader may clone or rename materials; retain the document's canonical name.
export function assignedMaterialReference(state, object, slot = 0) {
    const material = materialAssignments(object, slot, state.primitiveIndexByMesh)[0]?.material;
    if (!material) return null;
    for (const [id, candidate] of state.innoWorksMaterialsById ?? []) {
        if (candidate === material) return { MaterialId: id, MaterialName: candidate.name };
    }
    for (const [index, candidate] of state.materialsByIndex ?? []) {
        if (candidate === material) return { MaterialName: state.gltf.materials[index].name };
    }
    return material.name ? { MaterialName: material.name } : null;
}

export function materialSource(doc, key) {
    const [kind, index] = String(key).split(":");
    const record = kind === "record" ? doc.json.extensions[EXTENSION].Materials?.find(item => item.Id === Number(index)) : null;
    const gltf = kind === "gltf" ? doc.json.materials?.[Number(index)] : doc.json.materials?.find(item => item.name === record?.Name);
    if (!record && !gltf) throw new Error("Choose a material first.");
    return { record, gltf, name: record?.Name ?? gltf.name };
}

export function paintValues(material) {
    return { color: `#${material.color.getHexString(SRGBColorSpace)}`, metallic: material.metalness ?? 0,
        roughness: material.roughness ?? 0.5, clearcoat: material.clearcoat ?? 0, clearcoatRoughness: material.clearcoatRoughness ?? 0 };
}

export function validatePaint(values) {
    if (!/^#[0-9a-f]{6}$/i.test(values.color) || !["metallic", "roughness", "clearcoat", "clearcoatRoughness"]
        .every(key => Number.isFinite(values[key]) && values[key] >= 0 && values[key] <= 1)) throw new Error("Material values must be between 0 and 1, with a valid hex color.");
}

export function applyPaint(material, values) {
    validatePaint(values);
    material.color.set(values.color);
    material.metalness = values.metallic; material.roughness = values.roughness;
    material.clearcoat = values.clearcoat; material.clearcoatRoughness = values.clearcoatRoughness;
    material.needsUpdate = true;
}

// Duplicate definitions only; texture/image indices and unknown extensions survive.
export function savePaintMaterial(doc, key, values, { duplicate = true } = {}) {
    validatePaint(values);
    const source = key ? materialSource(doc, key) : { gltf: { pbrMetallicRoughness: { baseColorFactor: [1, 1, 1, 1] } } };
    if (source.gltf?.extensions?.KHR_materials_unlit) throw new Error("Unlit materials do not support the paint editor.");
    let name = source.name;
    if (duplicate || !key) {
        const base = String(values.name ?? "").trim();
        if (!base) throw new Error("Enter a material name.");
        const names = new Set([...(doc.json.materials ?? []).map(m => m.name), ...(doc.json.extensions[EXTENSION].Materials ?? []).map(m => m.Name)]);
        name = base; let suffix = 2; while (names.has(name)) name = `${base} (${suffix++})`;
    }
    const linear = new Color(values.color), srgb = linear.clone().convertLinearToSRGB();
    if (source.gltf) {
        const material = duplicate || !key ? copy(source.gltf) : source.gltf;
        material.name = name;
        const pbr = material.pbrMetallicRoughness = material.pbrMetallicRoughness ?? {};
        pbr.baseColorFactor = [linear.r, linear.g, linear.b, pbr.baseColorFactor?.[3] ?? 1];
        pbr.metallicFactor = values.metallic; pbr.roughnessFactor = values.roughness;
        if (values.clearcoat || values.clearcoatRoughness || material.extensions?.KHR_materials_clearcoat) {
            material.extensions = material.extensions ?? {};
            material.extensions.KHR_materials_clearcoat = { ...material.extensions.KHR_materials_clearcoat,
                clearcoatFactor: values.clearcoat, clearcoatRoughnessFactor: values.clearcoatRoughness };
            doc.json.extensionsUsed = [...new Set([...(doc.json.extensionsUsed ?? []), "KHR_materials_clearcoat"])];
        }
        if (duplicate || !key) { doc.json.materials = doc.json.materials ?? []; doc.json.materials.push(material); }
    }
    if (source.record) {
        const records = doc.json.extensions[EXTENSION].Materials;
        const record = duplicate ? copy(source.record) : source.record;
        const oldColor = record.Properties?.find(p => aliases.color.includes(String(p.Name).toLowerCase().replace(/^_/, "")));
        const alpha = oldColor?.VectorValue?.W ?? source.gltf?.pbrMetallicRoughness?.baseColorFactor?.[3] ?? 1;
        record.Properties = (record.Properties ?? []).filter(p => !Object.values(aliases).flat().includes(String(p.Name).toLowerCase().replace(/^_/, "")));
        record.Properties.push({ Name: "baseColorFactor", Type: "Color", VectorValue: { X: srgb.r, Y: srgb.g, Z: srgb.b, W: alpha } },
            ...[["metallicFactor", values.metallic], ["roughnessFactor", values.roughness], ["clearcoatFactor", values.clearcoat], ["clearcoatRoughnessFactor", values.clearcoatRoughness]]
                .map(([Name, FloatValue]) => ({ Name, Type: "Range", FloatValue })));
        record.Name = name;
        if (duplicate) { record.Id = Math.max(-1, ...records.map(m => m.Id)) + 1; records.push(record); }
        return { MaterialId: record.Id, MaterialName: name, ShaderName: record.ShaderName };
    }
    return { MaterialName: name };
}

export function materialSlots(doc, path) {
    // Paths are supplied by the hierarchy, including the empty model-root path.
    const rootName = doc.json.extensions[EXTENSION].RootName;
    let index = doc.json.nodes.findIndex(node => node.name === rootName);
    for (const name of path ? path.split("/") : []) index = doc.json.nodes[index]?.children?.find(child => doc.json.nodes[child].name === name) ?? -1;
    return (doc.json.meshes?.[doc.json.nodes[index]?.mesh]?.primitives ?? []).map((primitive, index) => ({ index, name: doc.json.materials?.[primitive.material]?.name ?? "" }));
}

export function materialUses(doc, key) {
    const { record, name } = materialSource(doc, key);
    return doc.sets.flatMap(set => set.kind === "Material" ? set.options.filter(option => record
        ? option.raw.MaterialId === record.Id : option.raw.MaterialId == null && option.raw.MaterialName === name)
        .map(option => `${set.raw.Material.ModelVistaName} / ${option.raw.DisplayName}`) : []);
}

export function materialSwatch(doc, reference) {
    try {
        const key = Number.isInteger(reference.MaterialId) ? `record:${reference.MaterialId}`
            : `gltf:${doc.json.materials?.findIndex(material => material.name === reference.MaterialName)}`;
        const { record, gltf } = materialSource(doc, key);
        const property = record?.Properties?.find(p => aliases.color.includes(String(p.Name).toLowerCase().replace(/^_/, "")));
        const value = property?.VectorValue;
        const color = value ? new Color().setRGB(value.X, value.Y, value.Z, property.Type === "Color" ? SRGBColorSpace : LinearSRGBColorSpace)
            : new Color(...(gltf?.pbrMetallicRoughness?.baseColorFactor ?? [1, 1, 1]).slice(0, 3));
        return `#${color.getHexString()}`;
    } catch { return "transparent"; }
}
